import { useState } from 'react';
import { UMButton, UMIcon, UMSidePanel } from '../../urbanmap/_components';
import { Pill } from '../proposition-c/ui';
import { fmt } from './domain';
import { useApplySync, useSyncDiff } from './hooks';
import { PC, UMR, caption } from './tokens';
import type { SyncChangeKind } from './types';

interface Props {
  projectId: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}

const KIND: Record<SyncChangeKind, { label: string; tone: 'success' | 'warning' | 'neutral'; icon: string }> = {
  added: { label: 'Ajouté', tone: 'success', icon: 'add' },
  removed: { label: 'Supprimé', tone: 'warning', icon: 'remove' },
  modified: { label: 'Modifié', tone: 'neutral', icon: 'edit' },
};

function Tile({ n, label, icon }: { n: number; label: string; icon: string }) {
  return (
    <div style={{ flex: 1, border: `1px solid ${UMR.border}`, borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 4, ...caption }}><UMIcon name={icon} size={14} color={UMR.textSecondary} />{label}</span>
      <strong style={{ fontSize: 20, lineHeight: '28px' }}>{fmt(n)}</strong>
    </div>
  );
}

/** Aperçu des écarts copie projet ↔ UrbanMap, puis synchronisation complète (non sélective). */
export function SyncPanel({ projectId, onClose, onDone }: Props) {
  const diff = useSyncDiff(projectId, true);
  const apply = useApplySync(projectId);
  const [done, setDone] = useState<{ added: number; removed: number; modified: number } | null>(null);
  const d = diff.data;
  const total = d ? d.added + d.removed + d.modified : 0;

  const run = () => {
    if (!d) return;
    const summary = { added: d.added, removed: d.removed, modified: d.modified };
    apply.mutate(undefined, {
      onSuccess: () => { setDone(summary); onDone(`Projet synchronisé avec UrbanMap · ${total} bâtiment${total > 1 ? 's' : ''} mis à jour`); },
    });
  };

  const footer = done || (d && total === 0)
    ? <UMButton onClick={onClose}>Fermer</UMButton>
    : (
      <>
        <UMButton variant="secondary" disabled={apply.isPending} onClick={onClose}>Annuler</UMButton>
        <UMButton icon="sync" disabled={!d || apply.isPending} onClick={run}>{apply.isPending ? 'Synchronisation…' : 'Synchroniser'}</UMButton>
      </>
    );

  return (
    <UMSidePanel side="right" width={460} title="Synchronisation UrbanMap" onClose={apply.isPending ? undefined : onClose} footer={footer}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 14 }}>
        {diff.isLoading && <div style={caption}>Comparaison de la copie avec UrbanMap…</div>}
        {diff.isError && <div style={{ color: PC.dangerText }}>Impossible de comparer avec UrbanMap. Réessayez.</div>}

        {done && (
          <div role="status" style={{ display: 'flex', gap: 12, padding: 16, background: PC.successBg, color: PC.successText }}>
            <UMIcon name="check_circle" size={24} color={PC.successText} />
            <div>
              <strong>Synchronisation terminée</strong>
              <div>{done.added} ajouté(s) · {done.removed} supprimé(s) · {done.modified} modifié(s). Copie datée d’aujourd’hui ; vos ratios et DJU sont conservés.</div>
            </div>
          </div>
        )}

        {!done && d && (
          <>
            <div style={caption}>Copie du projet du <strong>{d.snapshotDate}</strong> comparée à UrbanMap aujourd’hui, sur le périmètre du projet.</div>
            {total === 0 ? (
              <div role="status" style={{ display: 'flex', gap: 12, padding: 16, background: UMR.grey100 }}>
                <UMIcon name="task_alt" size={24} color={PC.accent} />
                <div><strong>Aucun écart</strong><div>Le projet est à jour avec UrbanMap.</div></div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Tile n={d.added} label="Ajoutés" icon="add" />
                  <Tile n={d.removed} label="Supprimés" icon="remove" />
                  <Tile n={d.modified} label="Modifiés" icon="edit" />
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, lineHeight: '16px' }}>
                  <thead>
                    <tr style={{ background: UMR.grey100, color: UMR.textLabel, textAlign: 'left' }}>
                      <th style={{ padding: 6 }}>ID UM</th>
                      <th style={{ padding: 6 }}>Champ</th>
                      <th style={{ padding: 6 }}>Projet → UrbanMap</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.changes.map((c, i) => (
                      <tr key={`${c.id}-${c.field}-${i}`} style={{ borderBottom: `1px solid ${UMR.borderSubtle}`, verticalAlign: 'top' }}>
                        <td style={{ padding: 6 }}>
                          <div style={{ fontFamily: 'monospace', color: UMR.blueLink }}>{c.id}</div>
                          <div style={{ marginTop: 2 }}><Pill tone={KIND[c.kind].tone} icon={KIND[c.kind].icon}>{KIND[c.kind].label}</Pill></div>
                        </td>
                        <td style={{ padding: 6 }}>{c.field}</td>
                        <td style={{ padding: 6 }}>
                          <span style={{ color: UMR.textSecondary, textDecoration: c.kind === 'removed' ? 'line-through' : undefined }}>{c.projectValue}</span>
                          {' → '}
                          <strong>{c.umValue}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', gap: 8, padding: 12, background: PC.warningBg, border: `1px solid ${PC.warningBorder}`, color: PC.warningText, fontSize: 13 }}>
                  <UMIcon name="info" size={18} color={PC.warningText} />
                  <span>« Synchroniser » applique <strong>tous</strong> les écarts (pas de choix bâtiment par bâtiment). Vos ratios et DJU sont conservés. « Annuler » garde la copie actuelle.</span>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </UMSidePanel>
  );
}
